import React, { ReactElement, ReactNode, useEffect } from 'react';
import cn from 'classnames';
import { Controller, FieldError, UnpackNestedValue } from 'react-hook-form';
import deepEqual from 'fast-deep-equal';
import _get from 'lodash.get';
import {
  Alert,
  Field,
  Select,
  TextAreaField,
  DatePickerField,
  RadioGroup,
  CheckList,
  Toggle,
  OptionRenderPropEnhancedArgs,
  CheckListOptionRenderPropEnhancedArgs,
  useDidUpdateEffect,
  CreatableSelect,
  AsyncSelect,
} from '@odin-labs/components';
import { Dropzone } from 'components/dropzone';
import { Player } from 'components/player';
import { HtmlRenderer } from 'components/htmlRenderer';
import { JobsiteWorkerDocumentUpload } from 'components/jobsiteWorkerDocument';
import { PlacesAutocomplete, Place } from 'components/placesAutocomplete';
import { Section } from 'components/section';
import { Panel } from 'components/panel';
import { Weather } from 'components/weather';
import { camelToKebabCase, objectEntries } from 'utils';

import type {
  CheckListFormOption,
  CustomValidateResult,
  FormData,
  FormInput,
  FormElementName,
  RadioGroupFormOption,
  UseFormBuilderArgs,
  UseFormBuilderResult,
  UseFormMethods,
  FormFieldInputRef,
} from './types';
import { FormInputTypes, TypedFormInputs, UseInputs } from './types';
import { getEnhancedInputs, resolveInputs, useResolvedInputs } from './utils';
import { FormRepeater } from './FormRepeater';
import { FormCheckbox } from './FormCheckbox';

export function useFormBuilder<TFields extends FormData>(
  args: UseFormBuilderArgs<TFields>,
): UseFormBuilderResult<TFields> {
  const {
    defaultValues,
    changedValues,
    onSubmit,
    onIsDirtyChange,
    validationTriggers = [],
    resetTrigger,
    autoFocus,
    fieldsConfig,
    localization,
  } = args;

  const inputRefs = React.useRef<
    Partial<Record<FormInput<TFields>['name'], FormFieldInputRef>>
  >({});

  const setRef = (options: {
    input: { name: FormInput<TFields>['name'] };
    inputRef: FormFieldInputRef;
    controllerRef: React.MutableRefObject<any>;
  }): void => {
    const { input, inputRef, controllerRef } = options;
    inputRefs.current[input.name] = inputRef;
    controllerRef.current = inputRef;
  };

  const getFieldRef = React.useCallback(
    <TFieldRef,>(fieldName: FormInput<TFields>['name']): TFieldRef => {
      return inputRefs.current[fieldName] as TFieldRef;
    },
    [],
  );

  const methods: UseFormMethods<TFields> = Object.assign(args.methods, {
    getFieldRef,
  });

  // assign methods to a ref to avoid closure issues caused by useMemo or useCallback
  const methodsRef = React.useRef(methods);
  methodsRef.current = methods;

  const getInputs = (
    inputsArg:
      | FormInput<TFields>[]
      | TypedFormInputs<TFields>
      | UseInputs<TFields>,
    fieldPrefix?: string,
  ): FormInput<TFields>[] => {
    const resolvedInputs = resolveInputs(
      inputsArg,
      methodsRef.current,
      fieldPrefix,
    );
    return getEnhancedInputs({
      inputs: resolvedInputs,
      config: fieldsConfig,
      localization,
      autoFocus,
    });
  };

  const resolvedInputs = useResolvedInputs(args.inputs, methodsRef.current);
  const inputs = React.useMemo(() => {
    return getEnhancedInputs({
      inputs: resolvedInputs,
      config: fieldsConfig,
      localization,
      autoFocus,
    });
  }, [resolvedInputs, fieldsConfig, localization, autoFocus]);

  const { isDirty, isSubmitted } = methodsRef.current?.formState ?? {};

  const previousFormValues = React.useRef(defaultValues);
  const formWasResetDueToDefaultValueChanges = React.useRef(false);
  useEffect((): void => {
    if (!deepEqual(previousFormValues.current, defaultValues)) {
      previousFormValues.current = defaultValues;
      methodsRef.current.reset(defaultValues);
      formWasResetDueToDefaultValueChanges.current = true;
    }
    if (changedValues) {
      objectEntries(changedValues as TFields).forEach(([fieldName, value]) => {
        methodsRef.current.setValue(fieldName, value, { shouldDirty: true });
      });
    }
  }, [defaultValues]);

  // trigger form reset when resetTrigger changes, if the form is dirty
  useEffect(() => {
    if (isDirty && !formWasResetDueToDefaultValueChanges.current) {
      methodsRef.current.reset(defaultValues);
    }
    formWasResetDueToDefaultValueChanges.current = false;
  }, [resetTrigger]);

  useDidUpdateEffect(() => {
    onIsDirtyChange?.(isDirty);
  }, [isDirty]);

  const isDirtyRef = React.useRef(isDirty);
  React.useEffect(() => {
    // cleanup function to set isDirty to false on unmount
    return (): void => {
      // only call onIsDirtyChange if the isDirty has actually changed
      if (isDirty !== isDirtyRef.current) {
        onIsDirtyChange?.(isDirty);
      }
    };
  }, []);
  isDirtyRef.current = isDirty;

  // trigger form validation when any of the validationTriggers changes, if the form has been previously submitted
  useEffect(() => {
    if (isSubmitted) {
      methodsRef.current.trigger();
    }
  }, [...validationTriggers]);

  const formOnSubmit = (
    data: UnpackNestedValue<TFields>,
    event: React.BaseSyntheticEvent<object, any, any>,
  ): void => {
    onSubmit(
      data,
      event,
      methodsRef.current.formState.dirtyFields,
      methodsRef.current,
    );
  };

  const getValidation = (
    input: FormInput<TFields>,
  ): FormInput<TFields>['validation'] => {
    const { name: inputName, validation } = input;
    const { validate } = validation;

    if (typeof validate === 'function') {
      const itemName = inputName.includes(']')
        ? inputName.substring(0, inputName.indexOf(']') + 1)
        : null;
      return {
        ...validation,
        validate: (
          data,
        ): CustomValidateResult | Promise<CustomValidateResult> => {
          const form = methodsRef.current;
          const formValues = itemName
            ? _get(form.getValues(), itemName)
            : form.getValues();
          return validate(data, formValues, form);
        },
      };
    }

    return validation;
  };

  const getInputError = (inputName: FormElementName<TFields>): string => {
    const error = _get(methodsRef.current.errors, inputName) as FieldError;
    return error?.message;
  };

  /**
   * @param input Gets current element and renders based off the FormInput type.
   */
  const getCurrentInput = (input: FormInput<TFields>): ReactNode => {
    const validation =
      input.validation &&
      (getValidation(input) as React.ComponentProps<
        typeof Controller
      >['rules']);

    const label =
      input.element !== FormInputTypes.Section &&
      input.element !== FormInputTypes.Panel
        ? input.elementProps?.label ?? input.label
        : undefined;
    // this is used by the `FormRepeater` only to set the default value of the input
    const defaultValueProp =
      input.defaultValue !== undefined
        ? { defaultValue: input.defaultValue }
        : undefined;

    switch (input.element) {
      case FormInputTypes.Field: {
        const { fieldType, cleaveOptions, inputMode, preventLeadingZeros } =
          input.elementProps ?? {};
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <Field
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  loading={input.loading}
                  disabled={input.elementProps?.disabled}
                  note={input.elementProps?.note}
                  placeholder={input.elementProps?.placeholder}
                  type={fieldType}
                  icon={input.elementProps?.icon}
                  showDefaultIcon={input.elementProps?.showDefaultIcon}
                  autoFocus={input.elementProps?.autoFocus}
                  maxLength={input.elementProps?.maxLength}
                  showLengthCounterNote={
                    input.elementProps?.showLengthCounterNote
                  }
                  innerRightLabel={input.elementProps?.innerRightLabel}
                  {...(fieldType === 'custom' ? { cleaveOptions } : undefined)}
                  {...(fieldType === 'number'
                    ? { preventLeadingZeros }
                    : undefined)}
                  {...(inputMode ? { inputMode } : undefined)}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.Toggle: {
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <Toggle
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  disabled={input.elementProps?.disabled}
                  description={input.elementProps?.toggleDescription}
                  toggleAlignment={input.elementProps?.toggleAlignment}
                  autoFocus={input.elementProps?.autoFocus}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.Checkbox: {
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <FormCheckbox
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  disabled={input.elementProps?.disabled}
                  description={input.elementProps?.toggleDescription}
                  autoFocus={input.elementProps?.autoFocus}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.TextAreaField:
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <TextAreaField
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  loading={input.loading}
                  disabled={input.elementProps?.disabled}
                  placeholder={input.elementProps?.placeholder}
                  autoFocus={input.elementProps?.autoFocus}
                  maxLength={input.elementProps?.maxLength}
                  showLengthCounterNote={
                    input.elementProps?.showLengthCounterNote
                  }
                  note={input.elementProps?.note}
                  size={input.elementProps?.size}
                  maxRows={input.elementProps?.maxRows}
                  autosize={input.elementProps?.autosize}
                />
              );
            }}
          />
        );
      case FormInputTypes.Select:
      case FormInputTypes.CreatableSelect:
      case FormInputTypes.AsyncSelect:
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              let SelectComponent: React.ElementType;
              switch (input.element) {
                case FormInputTypes.CreatableSelect:
                  SelectComponent = CreatableSelect;
                  break;
                case FormInputTypes.AsyncSelect:
                  SelectComponent = AsyncSelect;
                  break;
                default:
                  SelectComponent = Select;
              }

              return (
                <SelectComponent
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef: any) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  loading={input.loading}
                  isDisabled={input.elementProps?.disabled}
                  placeholder={input.elementProps?.placeholder}
                  icon={input.elementProps?.icon}
                  isMulti={input.elementProps?.isMulti}
                  clearToNull={input.elementProps?.clearToNull}
                  isClearable={input.elementProps?.isClearable}
                  autoFocus={input.elementProps?.autoFocus}
                  note={input.elementProps?.note}
                  closeMenuOnSelect={!input.elementProps?.isMulti}
                  {...(input.element === FormInputTypes.AsyncSelect
                    ? { loadOptions: input.elementProps?.loadOptions }
                    : { options: input.elementProps?.options })}
                />
              );
            }}
          />
        );
      case FormInputTypes.DatePicker: {
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <DatePickerField
                  {...input.elementProps}
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef: any) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  loading={input.loading}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.Dropzone: {
        const { isMulti, ...elementProps } = input.elementProps as Omit<
          typeof input.elementProps,
          'size' | 'content'
        >;
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <Dropzone
                  {...elementProps}
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  loading={input.loading}
                  multiple={isMulti}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.Player: {
        const elementProps = input.elementProps as React.ComponentProps<
          typeof Player
        >;
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <Player
                  {...elementProps}
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef: any) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.HtmlRenderer: {
        const elementProps = input.elementProps as React.ComponentProps<
          typeof HtmlRenderer
        >;
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <HtmlRenderer
                  {...elementProps}
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.Alert:
        return (
          <Alert
            {...(input.elementProps as unknown as React.ComponentProps<
              typeof Alert
            >)}
          />
        );
      case FormInputTypes.Weather:
        return (
          <Weather
            {...(input.elementProps as unknown as React.ComponentProps<
              typeof Weather
            >)}
          />
        );
      case FormInputTypes.CustomInput: {
        const { customInput: CustomInput, ...restInputProps } =
          input.elementProps;
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <CustomInput
                  {...restInputProps}
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  loading={input.loading}
                  disabled={input.elementProps?.disabled}
                  note={input.elementProps?.note}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.CustomContent: {
        const { content } = input.elementProps ?? {};
        if (typeof content === 'function') {
          return content();
        }
        return content;
      }
      case FormInputTypes.JobsiteWorkerDocument: {
        return (
          <JobsiteWorkerDocumentUpload
            label={label}
            {...(input.elementProps as React.ComponentProps<
              typeof JobsiteWorkerDocumentUpload
            >)}
            files={
              input.files &&
              getInputs(input.files).map((fileChild) => {
                return (
                  <div key={fileChild.name} className={cn(fileChild.layout)}>
                    {getCurrentInput(fileChild)}
                  </div>
                );
              })
            }
          >
            {getInputs(input.children).map((child) => {
              return (
                <div key={child.name} className={cn(child.layout)}>
                  {getCurrentInput(child)}
                </div>
              );
            })}
          </JobsiteWorkerDocumentUpload>
        );
      }
      case FormInputTypes.PlacesAutocomplete: {
        const { onCommit, ...elementProps } =
          input.elementProps as typeof input.elementProps &
            React.ComponentProps<typeof PlacesAutocomplete>;
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <PlacesAutocomplete
                  {...elementProps}
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  loading={input.loading}
                  onCommit={
                    onCommit &&
                    ((place: Place): void => {
                      onCommit(place, methodsRef.current);
                    })
                  }
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.RadioGroup: {
        const radioGroupOptions = (
          input.elementProps?.options as RadioGroupFormOption<
            TFields,
            string | number
          >[]
        ).map(
          ({
            inputs: optionInputs,
            inputsVisibility,
            inputsAvailability,
            inputsContainerLayout,
            inputsPosition,
            ...option
          }) => {
            if (optionInputs) {
              return {
                childrenPosition: inputsPosition,
                ...option,
                children: React.useCallback(
                  ({
                    checked,
                  }: OptionRenderPropEnhancedArgs<
                    string | number
                  >): React.ReactNode => {
                    const formElements = optionInputs?.map(
                      (optionInputConfig: FormInput<TFields>) => {
                        const optionInputElementProps =
                          optionInputConfig.elementProps as {
                            disabled?: boolean;
                            title: string; // for fixing SectionProps typing issues
                          };
                        const optionInput =
                          optionInputElementProps?.disabled === undefined &&
                          inputsAvailability === 'whenChecked'
                            ? {
                                ...optionInputConfig,
                                elementProps: {
                                  ...optionInputElementProps,
                                  disabled: !checked,
                                },
                              }
                            : optionInputConfig;

                        return (
                          <div
                            key={optionInput.name}
                            className={cn(optionInput.layout)}
                          >
                            {getCurrentInput(optionInput)}
                          </div>
                        );
                      },
                    );

                    if (inputsVisibility === 'always' || checked) {
                      return inputsContainerLayout ? (
                        <div className={inputsContainerLayout}>
                          {formElements}
                        </div>
                      ) : (
                        formElements
                      );
                    }
                    return null;
                  },
                  [
                    optionInputs,
                    inputsVisibility,
                    inputsAvailability,
                    inputsContainerLayout,
                  ],
                ),
              };
            }
            return option;
          },
        );
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <RadioGroup
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  error={getInputError(input.name)}
                  loading={input.loading}
                  {...input.elementProps}
                  // options are overridden to inject custom logic
                  // for rendering inputs based on selected option
                  options={radioGroupOptions}
                />
              );
            }}
          />
        );
      }
      case FormInputTypes.CheckList: {
        const checkListOptions = (
          input.elementProps?.options as CheckListFormOption<
            TFields,
            string | number
          >[]
        ).map(
          ({
            inputs: optionInputs,
            inputsVisibility,
            inputsContainerLayout,
            ...option
          }) => {
            if (optionInputs) {
              const formElements = optionInputs?.map(
                (optionInput: FormInput<TFields>) => {
                  return (
                    <div
                      key={optionInput.name}
                      className={cn(optionInput.layout)}
                    >
                      {getCurrentInput(optionInput)}
                    </div>
                  );
                },
              );

              return {
                ...option,
                children: React.useCallback(
                  ({
                    selected,
                  }: CheckListOptionRenderPropEnhancedArgs<
                    string | number
                  >): React.ReactNode => {
                    if (inputsVisibility === 'always' || selected) {
                      return inputsContainerLayout ? (
                        <div className={inputsContainerLayout}>
                          {formElements}
                        </div>
                      ) : (
                        formElements
                      );
                    }
                    return null;
                  },
                  [inputsVisibility, inputsContainerLayout, formElements],
                ),
              };
            }
            return option;
          },
        );
        return (
          <Controller
            control={methodsRef.current.control}
            name={input.name}
            rules={validation}
            {...defaultValueProp}
            render={(controllerProps) => {
              const { onChange, onBlur, value, ref } = controllerProps;
              return (
                <CheckList
                  name={input.name}
                  label={label}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  ref={(inputRef) => {
                    setRef({ input, inputRef, controllerRef: ref });
                  }}
                  {...input.elementProps}
                  // options are overridden to inject custom logic
                  // for rendering inputs based on selected option
                  options={checkListOptions}
                />
              );
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  const resolveElement = (input: FormInput<TFields>): ReactElement => {
    switch (input.element) {
      case FormInputTypes.Section: {
        const sectionChildren = getInputs(
          input.children,
          input.skipChildrenNamePrefix ? undefined : input.name,
        );
        return (
          <Section
            key={input.name}
            id={camelToKebabCase(input.name)}
            {...input.elementProps}
            className={cn(input.layout)}
          >
            {sectionChildren.map((child) => {
              return resolveElement(child);
            })}
          </Section>
        );
      }

      case FormInputTypes.Panel: {
        const panelChildren = getInputs(
          input.children,
          input.skipChildrenNamePrefix ? undefined : input.name,
        );
        return (
          <Panel
            key={input.name}
            {...input.elementProps}
            className={cn(input.layout)}
          >
            {panelChildren.map((child) => {
              return resolveElement(child);
            })}
          </Panel>
        );
      }

      case FormInputTypes.Repeater: {
        const repeaterInputs = getInputs(input.children, input.name);
        return (
          <FormRepeater
            key={input.name}
            name={input.name}
            className={cn(input.layout)}
            methods={methodsRef.current}
            inputs={repeaterInputs}
            resolveElement={resolveElement}
            {...input.elementProps}
          />
        );
      }

      case FormInputTypes.Layout:
        return (
          <div key={input.name} className={cn(input.layout)}>
            {getInputs(input.children).map((child) => {
              return resolveElement(child);
            })}
          </div>
        );

      default:
        return (
          <div key={input.name} className={cn(input.layout)}>
            {getCurrentInput(input)}
          </div>
        );
    }
  };

  const formElements = inputs?.map((input: FormInput<TFields>) => {
    return resolveElement(input);
  });

  return { formOnSubmit, formElements };
}
